// Users — VE user login & activity analytics.
//
// v1 reads cached login / message / referral rollups via the existing
// /api/cache/data endpoints (same feeds as Insights). No new backend
// aggregation yet — future versions can add a dedicated summary API.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangePicker from '../components/DateRangePicker';
import FacilityFilter from '../components/FacilityFilter';
import ScopeBar from '../components/ScopeBar';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import { PersonContactsProvider } from '../contexts/PersonContactsContext';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import { isTestRecord } from '../utils/testDataFilter';
import {
  buildUserProfiles,
  profilesToArray,
  filterLoginRow,
  filterMessageRow,
  filterReferralRow,
  yesterdayYmd,
} from '../utils/userAnalytics';
import DailyLoginsTab from './users/DailyLoginsTab';
import UserActivityTab from './users/UserActivityTab';
import UserTypesTab from './users/UserTypesTab';
import DeviceRepsTab from './users/DeviceRepsTab';
import UsersMapTab from './users/UsersMapTab';
import './CollaborationAnalytics.css';
import './Insights.css';
import './Users.css';

const TABS = [
  { id: 'daily', label: 'Daily Logins', hint: 'Who logged in on a specific day' },
  { id: 'activity', label: 'Activity', hint: 'Ranked users across logins, messages, referrals' },
  { id: 'types', label: 'User Types', hint: 'Breakdown by user_type from login cache' },
  { id: 'reps', label: 'Device Reps', hint: 'Medical device / vendor rep usage' },
  { id: 'map', label: 'Map', hint: 'Users by city/state' },
];

const VE_FACILITY_RX = /vitalengine|ve[\s-]?engineering|myheart/i;
const VE_NAME_RX = /vitalengine|ve[\s-]?engineering/i;
const NON_VE_PLACEHOLDER_FACILITY = /^non\s+vitalengine\s+user\s+facility$/i;

const f = (r, k) => (r?.data || r?.Data || {})[k];

function isVeFacilityLabel(name) {
  if (!name) return false;
  if (NON_VE_PLACEHOLDER_FACILITY.test(String(name).trim())) return false;
  return VE_FACILITY_RX.test(name);
}

function isVeLoginRow(record) {
  const facName = f(record, 'facility_name');
  const userName = f(record, 'user_display_name');
  return isVeFacilityLabel(facName) || (userName && VE_NAME_RX.test(userName));
}

function isVeReferralRow(record) {
  return (
    isVeFacilityLabel(f(record, 'sending_facility'))
    || isVeFacilityLabel(f(record, 'receiving_facility'))
    || isVeFacilityLabel(f(record, 'creating_facility'))
  );
}

function calculateDateRange(preset) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'yesterday':
      end.setDate(end.getDate() - 1);
      start.setDate(start.getDate() - 1);
      break;
    case 'last_week':
      start.setDate(start.getDate() - 7);
      break;
    case 'last_month':
      start.setDate(start.getDate() - 30);
      break;
    case 'last_3_months':
      start.setMonth(start.getMonth() - 3);
      break;
    case 'ytd':
      start.setMonth(0);
      start.setDate(1);
      break;
    case 'last_year':
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      break;
  }
  return { start, end };
}

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatDateShort(yyyymmdd) {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
}

function Users() {
  const cache = useCache();
  const { clearScope: clearGlobalScope } = useScope();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();

  const [activeTab, setActiveTab] = useState(() =>
    sessionStorage.getItem('users-active-tab') || 'daily'
  );
  const [dateRangePreset, setDateRangePreset] = useState('last_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activityDay, setActivityDay] = useState(yesterdayYmd());

  const [logins, setLogins] = useState([]);
  const [messages, setMessages] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');
  const [activeCustomerId, setActiveCustomerId] = useState('');

  const [removeTestData, setRemoveTestData] = useState(true);
  const [removeVeUsers, setRemoveVeUsers] = useState(true);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    sessionStorage.setItem('users-active-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r.success) setFacilityHierarchy(r.hierarchy || []);
      } catch { /* optional */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* optional */ }
    })();
    const { start, end } = calculateDateRange('last_month');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = calculateDateRange(dateRangePreset);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRangePreset]);

  const fetchOne = useCallback(async (label, type, s, e) => {
    try {
      const res = await cache.getCachedData(type, s, e);
      return res?.success ? (res.records || []) : [];
    } catch (err) {
      setErrors(prev => [...prev, `${label}: ${err.response?.data?.error || err.message}`]);
      return [];
    }
  }, [cache]);

  const loadAll = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrors([]);
    setHasLoaded(false);

    const tasks = [
      ['Logins', 'logins', startDate, endDate, setLogins],
      ['Messages', 'messages', startDate, endDate, setMessages],
      ['Referrals', 'referrals', startDate, endDate, setReferrals],
    ];

    setProgress({ current: 0, total: tasks.length, label: 'Starting…' });
    for (let i = 0; i < tasks.length; i++) {
      const [label, type, s, e, setter] = tasks[i];
      setProgress({ current: i, total: tasks.length, label });
      // eslint-disable-next-line no-await-in-loop
      const rows = await fetchOne(label, type, s, e);
      setter(rows);
    }
    setProgress({ current: tasks.length, total: tasks.length, label: 'Done' });
    setLoading(false);
    setHasLoaded(true);
    setFiltersCollapsed(true);
    setTimeout(() => setProgress(null), 1500);
  }, [startDate, endDate, fetchOne]);

  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: loadAll,
    ready: Boolean(startDate && endDate),
  });

  const applySavedFilter = useCallback((filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) setSelectedFacilityIds(new Set());
    else {
      const sf = savedFilters.find(s => s.id === parseInt(filterId, 10));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds));
    }
    setScopeFromFilter(filterId);
  }, [savedFilters, setScopeFromFilter]);

  const applyCustomerFilter = useCallback(async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) setSelectedFacilityIds(new Set());
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids));
    }
    setScopeFromCustomer(customerId);
  }, [resolveCustomerFacilityIds, setScopeFromCustomer]);

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of logins) {
      const id = f(r, 'facility_id');
      if (id != null) ids.add(String(id));
    }
    return ids;
  }, [logins]);

  const filterOpts = useMemo(() => ({
    facIds: selectedFacilityIds,
    removeTestData,
    removeVeUsers,
    isTestRecord,
    isVeRow: isVeLoginRow,
  }), [selectedFacilityIds, removeTestData, removeVeUsers]);

  const filteredLogins = useMemo(
    () => logins.filter(r => filterLoginRow(r, filterOpts)),
    [logins, filterOpts]
  );
  const filteredMessages = useMemo(
    () => messages.filter(r => filterMessageRow(r, filterOpts)),
    [messages, filterOpts]
  );
  const filteredReferrals = useMemo(
    () => referrals.filter(r => filterReferralRow(r, {
      ...filterOpts,
      isVeRow: isVeReferralRow,
    })),
    [referrals, filterOpts]
  );

  const allProfiles = useMemo(() => {
    const map = buildUserProfiles(filteredLogins, filteredMessages, filteredReferrals);
    return profilesToArray(map);
  }, [filteredLogins, filteredMessages, filteredReferrals]);

  const profiles = useMemo(() => {
    if (!typeFilter) return allProfiles;
    return allProfiles.filter(p => (p.userType || '') === typeFilter);
  }, [allProfiles, typeFilter]);

  const contactUserIds = useMemo(
    () => profiles.slice(0, 500).map(p => Number(p.userId)).filter(Boolean),
    [profiles]
  );

  const yearLabel = `${formatDateShort(startDate)} – ${formatDateShort(endDate)}`;

  const handleSelectType = (userType) => {
    setTypeFilter(userType);
    if (userType) setActiveTab('activity');
  };

  return (
    <PersonContactsProvider userIds={contactUserIds}>
      <div className="users-page">
        <div className="page-header">
          <div>
            <h2>User Reports</h2>
            <p className="users-subtitle">
              Daily VitalEngine login and activity analytics — who is using the platform,
              from which facilities, and how they collaborate. Data comes from cached login,
              message, and referral rollups (same sources as Insights and Raw Data).
            </p>
          </div>
        </div>

        <div className={`filter-strip-bar ${filtersCollapsed ? 'collapsed' : ''}`}>
          {!filtersCollapsed ? (
            <div className="controls-panel">
              <div className="date-range-selector">
                <label htmlFor="usersDatePreset">Date Range:</label>
                <select
                  id="usersDatePreset"
                  value={dateRangePreset}
                  onChange={(e) => setDateRangePreset(e.target.value)}
                  disabled={loading}
                  className="date-range-dropdown"
                >
                  <option value="yesterday">Yesterday</option>
                  <option value="last_week">Last 7 days</option>
                  <option value="last_month">Last 30 days</option>
                  <option value="last_3_months">Last 3 months</option>
                  <option value="ytd">Year to date</option>
                  <option value="last_year">Last 12 months</option>
                  <option value="custom">Custom range</option>
                </select>
              </div>

              {dateRangePreset === 'custom' && (
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  disabled={loading}
                />
              )}

              {savedFilters.length > 0 && (
                <div className="saved-filter-selector">
                  <label htmlFor="usersSavedFilter">Saved Filter:</label>
                  <select
                    id="usersSavedFilter"
                    value={activeFilterId}
                    onChange={(e) => applySavedFilter(e.target.value)}
                    className="date-range-dropdown"
                  >
                    <option value="">None (show all)</option>
                    {savedFilters.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {customers.length > 0 && (
                <div className="saved-filter-selector">
                  <label htmlFor="usersCustomerFilter">Customer:</label>
                  <select
                    id="usersCustomerFilter"
                    value={activeCustomerId}
                    onChange={(e) => applyCustomerFilter(e.target.value)}
                    className="date-range-dropdown"
                  >
                    <option value="">None (show all)</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <button type="button" className="filter-button" onClick={() => setShowFacilityFilter(true)}>
                Custom Filter{selectedFacilityIds.size > 0 ? ` (${selectedFacilityIds.size})` : ''}
              </button>

              <label className="checkbox-inline">
                <input type="checkbox" checked={removeTestData} onChange={(e) => setRemoveTestData(e.target.checked)} />
                Remove test data
              </label>
              <label className="checkbox-inline">
                <input type="checkbox" checked={removeVeUsers} onChange={(e) => setRemoveVeUsers(e.target.checked)} />
                Remove VitalEngine users
              </label>

              <button className="load-button" onClick={loadAll} disabled={loading || !startDate || !endDate}>
                {loading ? 'Loading…' : 'Load Data'}
              </button>
            </div>
          ) : (
            <button type="button" className="filter-strip-toggle" onClick={() => setFiltersCollapsed(false)}>
              Edit filters · {formatDateShort(startDate)} – {formatDateShort(endDate)}
            </button>
          )}
        </div>

        {startDate && endDate && (
          <p className="insights-date-hint">
            Loaded window: <strong>{formatDateShort(startDate)}</strong> – <strong>{formatDateShort(endDate)}</strong>
            {typeFilter && (
              <span className="insights-date-hint-sub">
                {' '}· filtered to user type &quot;{typeFilter}&quot;
                {' '}
                <button type="button" className="users-clear-filter" onClick={() => setTypeFilter('')}>
                  Clear
                </button>
              </span>
            )}
          </p>
        )}

        <ScopeBar
          scope={currentScope}
          onViewFacilities={
            currentScope.kind !== 'none' && facilityHierarchy?.length && selectedFacilityIds.size
              ? () => setShowScopeFacilities(true)
              : null
          }
        />

        {progress && (
          <div className="load-progress">
            {progress.label} ({progress.current}/{progress.total})
          </div>
        )}

        {errors.length > 0 && (
          <div className="error-banner">
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        )}

        {!hasLoaded && !loading && (
          <div className="empty-state">
            <p>Select a date range and scope, then click <strong>Load Data</strong>.</p>
            <p className="users-empty-hint">
              Tip: use <strong>Year to date</strong> on the Device Reps tab to answer
              &quot;how many medical device reps logged in this year?&quot;
            </p>
          </div>
        )}

        {hasLoaded && (
          <>
            <p className="users-loaded-summary">
              <strong>{filteredLogins.length.toLocaleString()}</strong> login rollups ·{' '}
              <strong>{profiles.length.toLocaleString()}</strong> unique users
            </p>

            <nav className="insights-tabs" role="tablist" aria-label="Users analysis views">
              {TABS.map(t => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={activeTab === t.id}
                  className={`insights-tab ${activeTab === t.id ? 'is-active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                  title={t.hint}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            <div role="tabpanel">
              {activeTab === 'daily' && (
                <DailyLoginsTab
                  loginRows={filteredLogins}
                  activityDay={activityDay}
                  onActivityDayChange={setActivityDay}
                  userTypeFilter={typeFilter}
                />
              )}
              {activeTab === 'activity' && <UserActivityTab profiles={profiles} />}
              {activeTab === 'types' && (
                <UserTypesTab profiles={allProfiles} onSelectType={handleSelectType} />
              )}
              {activeTab === 'reps' && (
                <DeviceRepsTab profiles={allProfiles} yearLabel={yearLabel} />
              )}
              {activeTab === 'map' && <UsersMapTab profiles={allProfiles} />}
            </div>
          </>
        )}

        {showFacilityFilter && (
          <FacilityFilter
            hierarchy={facilityHierarchy}
            availableFacilityIds={dataFacilityIds}
            selectedFacilityIds={selectedFacilityIds}
            onSelectionChange={(ids) => {
              setSelectedFacilityIds(ids);
              setActiveFilterId('');
              setActiveCustomerId('');
            }}
            onClose={() => setShowFacilityFilter(false)}
          />
        )}

        {showScopeFacilities && currentScope.kind !== 'none' && (
          <GroupFacilitiesModal
            groupName={currentScope.name}
            hierarchy={facilityHierarchy}
            facilityIds={Array.from(selectedFacilityIds)}
            onClose={() => setShowScopeFacilities(false)}
          />
        )}
      </div>
    </PersonContactsProvider>
  );
}

export default Users;
